import { useCallback, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  clearAllBoundingBoxes,
  clearInitialBoundingBoxes,
  clearRedoStack,
  clearUndoStack,
  selectAllBoundingBoxes,
  selectAllUntouchedBoundingBoxes,
  selectAcceptedBoundingBoxes,
  selectRejectedBoundingBoxes,
  setSelectedBoundingBox,
  setRejectedBoundingBoxes,
  setAcceptedBoundingBoxes,
  selectedBBoxClassIndex,
} from '../../../../store/slices/annotationSlice'
import { AnnotationDataBoundingBox } from '../../../../types/models/annotation'

export function usePredictionReview() {
  const dispatch = useAppDispatch()
  const allBoundingBoxes = useAppSelector(selectAllBoundingBoxes)
  const allUntouchedBBoxes = useAppSelector(selectAllUntouchedBoundingBoxes)
  const acceptedBBoxes = useAppSelector(selectAcceptedBoundingBoxes)
  const rejectedBBoxes = useAppSelector(selectRejectedBoundingBoxes)

  const [selectedSnippetIndex, setSelectedSnippetIndex] = useState<number | undefined>(undefined)
  const [selectedSnippetArrayIndex, setSelectedSnippetArrayIndex] = useState<number>(-1)
  const [hoveredBoxIndex, setHoveredBoxIndex] = useState<number | undefined>(undefined)
  type PredictionTab = 'inspect' | 'accepted' | 'rejected'
  const [activeRightTab, setActiveRightTab] = useState<PredictionTab>('inspect')
  const [showShortcuts, setShowShortcuts] = useState(false)
  const selectedBBoxClassIdx = useAppSelector(selectedBBoxClassIndex)

  const resetForNextImage = useCallback(() => {
    dispatch(clearInitialBoundingBoxes())
    dispatch(clearAllBoundingBoxes())
    setSelectedSnippetIndex(undefined)
    setSelectedSnippetArrayIndex(-1)
    dispatch(setSelectedBoundingBox(undefined))
    dispatch(setRejectedBoundingBoxes([]))
    dispatch(setAcceptedBoundingBoxes([]))
    dispatch(clearUndoStack())
    dispatch(clearRedoStack())
  }, [dispatch])

  const getCurrentSelectedBox = useCallback((): AnnotationDataBoundingBox | undefined => {
    return allBoundingBoxes.find((b) => b.class_index === selectedBBoxClassIdx)
  }, [selectedBBoxClassIdx, allBoundingBoxes])

  const selectNextUntouched = useCallback(() => {
    const untouchedBoxIndices = allUntouchedBBoxes
      .map((box) => allBoundingBoxes.findIndex((b) => b.class_index === box.class_index))
      .filter((index) => index !== -1)
    if (untouchedBoxIndices.length === 0) return
    if (selectedSnippetIndex === undefined) {
      const firstBoxIndex = untouchedBoxIndices[0]
      setSelectedSnippetIndex(firstBoxIndex)
      const firstBox = allBoundingBoxes[firstBoxIndex]
      if (firstBox) {
        dispatch(setSelectedBoundingBox(firstBox))
        const arrayIndex = allUntouchedBBoxes.findIndex(
          (b) => b.class_index === firstBox.class_index
        )
        setSelectedSnippetArrayIndex(arrayIndex >= 0 ? arrayIndex : 0)
      }
    } else {
      const currentBoxClassIndex = allBoundingBoxes[selectedSnippetIndex]?.class_index
      const currentArrayIndex = allUntouchedBBoxes.findIndex(
        (b) => b.class_index === currentBoxClassIndex
      )
      const nextArrayIndex =
        currentArrayIndex === -1 || currentArrayIndex >= allUntouchedBBoxes.length - 1
          ? 0
          : currentArrayIndex + 1
      const nextBox = allUntouchedBBoxes[nextArrayIndex]
      if (nextBox) {
        const nextBoxIndex = allBoundingBoxes.findIndex(
          (b) => b.class_index === nextBox.class_index
        )
        setSelectedSnippetIndex(nextBoxIndex)
        dispatch(setSelectedBoundingBox(nextBox))
        setSelectedSnippetArrayIndex(nextArrayIndex)
      }
    }
  }, [
    allUntouchedBBoxes,
    allBoundingBoxes,
    selectedSnippetIndex,
    dispatch,
    setSelectedSnippetArrayIndex,
  ])

  return {
    // state
    selectedSnippetIndex,
    setSelectedSnippetIndex,
    selectedSnippetArrayIndex,
    setSelectedSnippetArrayIndex,
    hoveredBoxIndex,
    setHoveredBoxIndex,
    activeRightTab,
    setActiveRightTab,
    showShortcuts,
    setShowShortcuts,
    // selectors
    allBoundingBoxes,
    allUntouchedBBoxes,
    acceptedBBoxes,
    rejectedBBoxes,
    // helpers
    resetForNextImage,
    getCurrentSelectedBox,
    selectNextUntouched,
  }
}
